import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  BiosBadge,
  BiosFooter,
  BiosHeader,
  BiosLine,
  BiosScreen,
  BootOverlay,
  Cursor,
  LoginBar,
  LoginBrand,
  LoginDivider,
  LoginHint,
  LoginMain,
  LoginName,
  LoginScreen,
  LoginTagline,
  LoginUsers,
  PhaseLayer,
  Scanlines,
  SkipButton,
  SplashCopyright,
  SplashLogo,
  SplashLogoRow,
  SplashScreen,
  SplashSubtitle,
  SplashTitle,
  UserAvatar,
  UserName,
  UserSub,
  UserTile,
  WelcomeText,
  XPProgress,
  XPProgressBlocks,
} from "./BootSequenceStyles";
import DecryptedText from "../Effects/DecryptedText";
import { PROFILE } from "../../config/profile";
import { playStartupChime } from "../../utils/sound";
import windowsIcon from "../../assets/images/windows-0.png";
import avatarIcon from "../../assets/images/msn3-4.png";

import { LoadStatus } from "../../pages/HomePage/utils/DesktopContext";

export type { LoadStatus };
type Phase = "bios" | "splash" | "login" | "welcome";

interface BootSequenceProps {
  projectTitles: string[];
  status: LoadStatus;
  onComplete: () => void;
}

interface BiosEntry {
  text: string;
  tone?: "ok" | "warn" | "accent";
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// "My Cool Project!" -> "MYCOOLPR.EXE", proper 8.3 DOS filenames
const toDosFileName = (title: string) =>
  `${
    title
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 8) || "PROJECT"
  }.EXE`;

const MAX_LISTED_PROJECTS = 8;
const CMS_TIMEOUT_MS = 4000;

const Bios = ({
  projectTitles,
  status,
  onDone,
}: {
  projectTitles: string[];
  status: LoadStatus;
  onDone: () => void;
}) => {
  const [lines, setLines] = useState<BiosEntry[]>([]);
  const [memory, setMemory] = useState<number | null>(null);
  const statusRef = useRef(status);
  const titlesRef = useRef(projectTitles);
  statusRef.current = status;
  titlesRef.current = projectTitles;

  useEffect(() => {
    let cancelled = false;
    const push = (entry: BiosEntry) => {
      if (!cancelled) setLines((prev) => [...prev, entry]);
    };

    const run = async () => {
      setLines([]);
      setMemory(null);
      await wait(900); // let the CRT warm up

      push({
        text: `${PROFILE.osName} Modular BIOS v4.20, An Energy Star Ally`,
        tone: "accent",
      });
      push({ text: "Copyright (C) 1998-2025, Portfolio Systems Inc." });
      push({ text: "" });
      await wait(250);
      push({ text: "Main Processor : Creativity(tm) CPU at 4200MHz" });
      await wait(150);

      // RAM count-up
      const target = 65536;
      const steps = 18;
      for (let i = 1; i <= steps && !cancelled; i++) {
        setMemory(Math.round((target / steps) * i));
        await wait(35);
      }
      if (cancelled) return;
      setMemory(null);
      push({ text: `Memory Testing : ${target}K OK`, tone: "ok" });
      push({ text: "" });
      await wait(200);

      push({ text: "Detecting Primary Master   ... HYGRAPH-CMS" });
      await wait(180);
      push({ text: "Detecting Primary Slave    ... GAMES.DSK" });
      await wait(180);
      push({ text: "" });
      push({ text: "Loading portfolio index" });

      // Wait for Hygraph (or give up gracefully)
      const started = Date.now();
      while (
        !cancelled &&
        statusRef.current === "loading" &&
        Date.now() - started < CMS_TIMEOUT_MS
      ) {
        await wait(100);
      }
      if (cancelled) return;

      const titles = titlesRef.current;
      if (statusRef.current === "ready" && titles.length > 0) {
        for (const title of titles.slice(0, MAX_LISTED_PROJECTS)) {
          if (cancelled) return;
          push({
            text: `  C:\\PROJECTS\\${toDosFileName(title).padEnd(14, ".")}..... OK`,
          });
          await wait(90);
        }
        if (titles.length > MAX_LISTED_PROJECTS) {
          push({
            text: `  ... and ${titles.length - MAX_LISTED_PROJECTS} more`,
          });
        }
        push({ text: "" });
        push({
          text: `${titles.length} project(s) found. System ready.`,
          tone: "ok",
        });
      } else {
        push({ text: "" });
        push({
          text: "WARNING: Project server not responding. Booting anyway...",
          tone: "warn",
        });
      }

      await wait(900);
      if (!cancelled) onDone();
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BiosScreen>
      <BiosHeader>
        <div>
          {lines.map((line, i) => (
            <BiosLine key={i} $tone={line.tone}>
              {line.text}
            </BiosLine>
          ))}
          {memory !== null && <BiosLine>Memory Testing : {memory}K</BiosLine>}
          <Cursor />
        </div>
        <BiosBadge>
          ★ ENERGY ★
          <br />
          PORTFOLIO
        </BiosBadge>
      </BiosHeader>
      <BiosFooter>Press ESC to skip intro</BiosFooter>
    </BiosScreen>
  );
};

const Splash = ({ onDone }: { onDone: () => void }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 2600);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <SplashScreen>
      <motion.div
        initial={{ opacity: 0, scale: 0.9, filter: "blur(8px)" }}
        animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
        transition={{ duration: 0.9, ease: "easeOut" }}
      >
        <SplashLogoRow>
          <SplashLogo src={windowsIcon} alt="" />
          <SplashTitle>
            {PROFILE.osName}
            <sup>xp</sup>
          </SplashTitle>
        </SplashLogoRow>
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.6 }}
      >
        <SplashSubtitle>Portfolio Edition</SplashSubtitle>
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
      >
        <XPProgress>
          <XPProgressBlocks>
            <span />
            <span />
            <span />
          </XPProgressBlocks>
        </XPProgress>
      </motion.div>
      <SplashCopyright>
        Copyright © {PROFILE.name}. All pixels reserved.
      </SplashCopyright>
    </SplashScreen>
  );
};

const Login = ({ onEnter }: { onEnter: () => void }) => {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Enter") onEnter();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onEnter]);

  return (
    <LoginScreen>
      <LoginBar $position="top" />
      <LoginMain>
        <LoginBrand>
          <LoginName>
            <DecryptedText text={PROFILE.name} startDelay={300} speed={45} />
          </LoginName>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.5 }}
          >
            <LoginTagline>{PROFILE.tagline}</LoginTagline>
            <LoginHint>To begin, click your user name</LoginHint>
          </motion.div>
        </LoginBrand>
        <LoginDivider />
        <LoginUsers>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{
              delay: 0.6,
              type: "spring",
              stiffness: 180,
              damping: 18,
            }}
          >
            <UserTile onClick={onEnter} autoFocus>
              <UserAvatar src={avatarIcon} alt="" />
              <div>
                <UserName>{PROFILE.visitorName}</UserName>
                <UserSub>Click or press Enter to explore</UserSub>
              </div>
            </UserTile>
          </motion.div>
        </LoginUsers>
      </LoginMain>
      <LoginBar $position="bottom">
        <span>Double-click icons · Right-click projects for details</span>
        <span>Esc to skip</span>
      </LoginBar>
    </LoginScreen>
  );
};

const phaseTransition = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.45 },
};

const BootSequence = ({
  projectTitles,
  status,
  onComplete,
}: BootSequenceProps) => {
  const [phase, setPhase] = useState<Phase>("bios");
  const completedRef = useRef(false);

  const finish = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete();
  }, [onComplete]);

  const handleEnter = useCallback(() => {
    playStartupChime();
    setPhase("welcome");
  }, []);

  useEffect(() => {
    if (phase !== "welcome") return;
    const t = setTimeout(finish, 2000);
    return () => clearTimeout(t);
  }, [phase, finish]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") finish();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [finish]);

  const goToSplash = useCallback(() => setPhase("splash"), []);
  const goToLogin = useCallback(() => setPhase("login"), []);

  return (
    <BootOverlay>
      <AnimatePresence mode="wait">
        {phase === "bios" && (
          <PhaseLayer key="bios" {...phaseTransition}>
            <Bios
              projectTitles={projectTitles}
              status={status}
              onDone={goToSplash}
            />
          </PhaseLayer>
        )}
        {phase === "splash" && (
          <PhaseLayer key="splash" {...phaseTransition}>
            <Splash onDone={goToLogin} />
          </PhaseLayer>
        )}
        {(phase === "login" || phase === "welcome") && (
          <PhaseLayer key="login" {...phaseTransition}>
            <LoginScreen style={{ position: "absolute" }}>
              <LoginBar $position="top" />
              <div />
              <LoginBar $position="bottom" />
            </LoginScreen>
            <AnimatePresence mode="wait">
              {phase === "login" ? (
                <PhaseLayer
                  key="users"
                  exit={{ opacity: 0, transition: { duration: 0.25 } }}
                >
                  <Login onEnter={handleEnter} />
                </PhaseLayer>
              ) : (
                <PhaseLayer
                  key="welcome"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={{ background: "#5a7edc", top: "13%", bottom: "13%" }}
                >
                  <WelcomeText>
                    <motion.span
                      initial={{ opacity: 0, x: -40 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        type: "spring",
                        stiffness: 120,
                        damping: 14,
                      }}
                    >
                      welcome
                    </motion.span>
                  </WelcomeText>
                </PhaseLayer>
              )}
            </AnimatePresence>
          </PhaseLayer>
        )}
      </AnimatePresence>
      <Scanlines />
      {(phase === "bios" || phase === "splash") && (
        <SkipButton onClick={finish}>Skip intro ▸</SkipButton>
      )}
    </BootOverlay>
  );
};

export default BootSequence;
